import { ImageIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import type { MemberPost } from "@/types/mission-detail"

/**
 * OKS-390 — Community strip: a horizontal-scroll preview of approved members'
 * posts for this mission. Intra-brand social proof (every post belongs to the
 * creator's own brand), so creator names ARE shown.
 *
 * Rendered only when there is ≥1 approved post — the caller omits the section
 * entirely on an empty array (this component still guards defensively). Tiles
 * are fixed 3:4, lazy-loaded; a null thumbnail degrades to a muted fallback
 * tile (no broken image).
 *
 * OKS-757 — each tile is now an external link opening the member's post on
 * TikTok in a new tab. The whole tile content (media block + creator caption)
 * is wrapped in a single keyboard-accessible `<a>` with a descriptive
 * `aria-label`; the OKS-756 two-outcome media rule (cover `<img>` vs muted
 * `ImageIcon` placeholder) is preserved byte-identically inside the wrap.
 * When `tiktok_video_url` is falsy (defensive only — it is presence-validated
 * on `Submission`), the tile renders as a plain non-interactive container:
 * no `<a href>`, not focusable, no link role — never a dead link.
 */
export interface CommunityStripProps {
  posts: MemberPost[]
}

export function CommunityStrip({ posts }: CommunityStripProps) {
  const { t } = useTranslation()

  if (posts.length === 0) {
    return null
  }

  return (
    <section
      data-testid="mission-detail-community"
      className="flex flex-col gap-2"
    >
      <h2 className="text-sm font-semibold">
        {t("creator_mission_detail.community.title")}
      </h2>
      <ul
        className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1"
        data-testid="mission-detail-community-list"
      >
        {posts.map((post) => {
          // Byte-identical inner content shared by the interactive (<a>) and
          // defensive non-interactive branches — preserves the OKS-756
          // two-outcome media rule + caption in both cases.
          const tileContent = (
            <>
              <div className="bg-muted aspect-[3/4] w-24 overflow-hidden rounded-lg">
                {post.thumbnail_url ? (
                  <img
                    src={post.thumbnail_url}
                    alt={t("creator_mission_detail.community.thumbnail_alt", {
                      creator: post.creator_name,
                    })}
                    loading="lazy"
                    decoding="async"
                    data-testid="mission-detail-community-thumb"
                    className="size-full object-cover"
                  />
                ) : (
                  <div
                    className="text-muted-foreground flex size-full items-center justify-center"
                    data-testid="mission-detail-community-thumb-fallback"
                    aria-label={t(
                      "creator_mission_detail.community.thumbnail_alt",
                      { creator: post.creator_name },
                    )}
                  >
                    <ImageIcon className="size-6" aria-hidden="true" />
                  </div>
                )}
              </div>
              <p className="text-muted-foreground mt-1 w-24 truncate text-xs">
                {post.creator_name}
              </p>
            </>
          )

          return (
            <li key={post.id} className="shrink-0">
              {post.tiktok_video_url ? (
                <a
                  href={post.tiktok_video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mission-detail-community-link"
                  aria-label={t(
                    "creator_mission_detail.community.open_post_aria",
                    { creator: post.creator_name },
                  )}
                  className={cn(
                    "block cursor-pointer rounded-lg",
                    "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                  )}
                >
                  {tileContent}
                </a>
              ) : (
                <div>{tileContent}</div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
